/**
 * FrameView — der Editor eines Beitrags zum Einbetten ins Portal
 * (/frame/post/:id, #2574, Post 5623).
 *
 * Wofür: E-Mail-Entwürfe in der Leiste „Entwürfe“ des Portals. Dort markiert
 * der Mensch Text, setzt Anmerkungen und entscheidet über die Karte
 * `draft_review` in cloud-api. Die Leseansicht /embed/post/:id kann das
 * nicht, sie zeigt nur an.
 *
 * Anmeldung, wie bei EmbedView, nur über das Portal:
 *  1. Beim Start geht `{type: 'embed-ready'}` an window.parent.
 *  2. Das Portal antwortet mit `{type: 'embed-auth', token}`. Angenommen
 *     wird die Antwort nur von FRAME_TRUSTED_ORIGINS.
 *  3. Mit dem Token holt FrameView das Profil (`/auth/me`) und übergibt
 *     beides an auth.ts, nur im Speicher (EMBEDDED_FRAME).
 *  4. Läuft das Token ab (401), fragt refreshAccessToken() über ein neues
 *     `embed-ready` beim Portal nach, statt auf die Anmeldeseite zu fallen.
 *
 * Ohne Antwort des Portals bleibt die Seite bei einem Hinweis stehen. Sie
 * fällt nie auf die Anmeldung des Browsers zurück.
 *
 * Aussehen und Höhe (Post 5623, Alex' zweiter Test):
 *  - `{type: 'embed-thema', stil, thema, vars}` vom Portal: Stil und
 *    CSS-Variablen werden auf <html> gesetzt, damit Editor und
 *    Anmerkungsfenster wie das Portal aussehen. Übernommen werden nur
 *    Werte für Variablen (`--name`), nie Regeln oder HTML.
 *  - `{type: 'embed-hoehe', hoehe}` an das Portal beim Laden und bei jeder
 *    Größenänderung, damit der Rahmen ohne inneren Scrollbalken passt.
 */

import { useEffect, useState, useSyncExternalStore } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { PostEditor } from './PostEditor'
import { AUTH_API, FRAME_TRUSTED_ORIGINS } from '../config'
import { applyStil } from '../lib/stil'
import {
  isLoggedIn,
  refreshAccessToken,
  setEmbeddedSession,
  setEmbeddedTokenRequester,
  subscribe,
  type AuthUser,
} from '../auth'

const HANDSHAKE_TIMEOUT_MS = 5000
// Tokens der auth-api leben 15 min; der WebSocket liest bei jedem
// Wiederverbinden das aktuelle, deshalb vorher beim Portal erneuern.
const RENEW_INTERVAL_MS = 10 * 60 * 1000

interface EmbedAuthMessage {
  type: 'embed-auth'
  token: string | null
}

function isEmbedAuth(data: unknown): data is EmbedAuthMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'embed-auth'
  )
}

// Herkunft des Portals, das die Anmeldung gereicht hat. Nur dorthin geht
// die Höhe des Inhalts.
let parentOrigin: string | null = null

const VAR_NAME = /^--[a-z][a-z0-9-]{0,47}$/
// Ein Wert darf keine Regel beenden oder öffnen und nichts nachladen.
const VAR_VALUE_FORBIDDEN = /[;{}<>\\]|url\(|expression\(|@import|javascript:/i
const MAX_VARS = 80

interface EmbedThemaMessage {
  type: 'embed-thema'
  stil?: unknown
  thema?: unknown
  vars?: unknown
}

function isEmbedThema(data: unknown): data is EmbedThemaMessage {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { type?: unknown }).type === 'embed-thema'
  )
}

/** Stil, Hell/Dunkel und CSS-Variablen des Portals übernehmen. */
export function applyEmbedThema(msg: EmbedThemaMessage, root: HTMLElement = document.documentElement) {
  if (msg.stil === 'v2' || msg.stil === 'v3') applyStil(msg.stil)
  if (msg.thema === 'dark' || msg.thema === 'light') root.setAttribute('data-theme', msg.thema)
  else root.removeAttribute('data-theme')
  if (!msg.vars || typeof msg.vars !== 'object') return
  let n = 0
  for (const [name, value] of Object.entries(msg.vars as Record<string, unknown>)) {
    if (n >= MAX_VARS) break
    if (!VAR_NAME.test(name)) continue
    if (typeof value !== 'string' || value.length > 200 || VAR_VALUE_FORBIDDEN.test(value)) continue
    root.style.setProperty(name, value.trim())
    n++
  }
}

/** Ein frisches Token beim Portal anfragen; null bei Ablehnung oder Zeitablauf. */
function requestTokenFromParent(postId: string | undefined): Promise<string | null> {
  return new Promise((resolve) => {
    if (window.parent === window) {
      resolve(null)
      return
    }
    const timer = window.setTimeout(() => finish(null), HANDSHAKE_TIMEOUT_MS)
    function onMessage(event: MessageEvent) {
      if (!FRAME_TRUSTED_ORIGINS.includes(event.origin)) return
      if (!isEmbedAuth(event.data)) return
      parentOrigin = event.origin
      finish(typeof event.data.token === 'string' && event.data.token ? event.data.token : null)
    }
    function finish(token: string | null) {
      window.clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      resolve(token)
    }
    window.addEventListener('message', onMessage)
    // Das Portal kennt seine Herkunft selbst und antwortet nur ihr; die
    // Anfrage trägt kein Geheimnis, deshalb ohne festes targetOrigin.
    window.parent.postMessage({ type: 'embed-ready', postId }, '*')
  })
}

async function fetchMe(token: string): Promise<AuthUser | null> {
  try {
    const resp = await fetch(`${AUTH_API}/api/v1/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!resp.ok) return null
    return (await resp.json()) as AuthUser
  } catch {
    return null
  }
}

export function FrameView() {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  // `?seit=<doc_version>`: zuletzt gesehene Fassung, Neueres wird markiert.
  const seitRaw = params.get('seit')
  const seit = seitRaw !== null && /^\d+$/.test(seitRaw) ? Number(seitRaw) : undefined
  const authed = useSyncExternalStore(subscribe, isLoggedIn)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    setEmbeddedTokenRequester(() => requestTokenFromParent(id))
    void (async () => {
      const token = await requestTokenFromParent(id)
      const user = token ? await fetchMe(token) : null
      if (!alive) return
      if (token && user) setEmbeddedSession(token, user)
      else setFailed(true)
    })()
    return () => {
      alive = false
      setEmbeddedTokenRequester(null)
    }
  }, [id])

  // Eingebettet: eigene Optik für Anmerkungsfenster und Knöpfe (global.css).
  useEffect(() => {
    document.documentElement.setAttribute('data-eingebettet', '')
    return () => document.documentElement.removeAttribute('data-eingebettet')
  }, [])

  // Aussehen vom Portal, jederzeit (auch nach einem Wechsel Hell/Dunkel).
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!FRAME_TRUSTED_ORIGINS.includes(event.origin)) return
      if (isEmbedThema(event.data)) applyEmbedThema(event.data)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Höhe des Inhalts an das Portal melden.
  useEffect(() => {
    if (!authed || window.parent === window) return
    let last = -1
    let watched: HTMLElement | null = null
    // Der Editor scrollt in `.workspace`; die Seite selbst ist immer so hoch
    // wie der Rahmen. Gemessen wird deshalb der Inhalt darin.
    const report = () => {
      const inner = document.querySelector<HTMLElement>('.workspace-inner')
      if (!inner || !parentOrigin) return
      if (inner !== watched) {
        watched = inner
        ro.observe(inner)
      }
      const hoehe = Math.ceil(inner.getBoundingClientRect().top + inner.scrollHeight)
      if (hoehe === last) return
      last = hoehe
      window.parent.postMessage({ type: 'embed-hoehe', hoehe }, parentOrigin)
    }
    const ro = new ResizeObserver(() => report())
    ro.observe(document.body)
    // Der Inhalt wächst ohne Größenänderung von <body> (CRDT, Anmerkungen).
    const mo = new MutationObserver(report)
    mo.observe(document.body, { childList: true, subtree: true, characterData: true })
    report()
    return () => {
      ro.disconnect()
      mo.disconnect()
    }
  }, [authed])

  useEffect(() => {
    if (!authed) return
    const timer = window.setInterval(() => { void refreshAccessToken() }, RENEW_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [authed])

  if (authed) return <PostEditor embedded changesSince={seit} />

  return (
    <div className="frame-wait" style={{ padding: 24, color: 'var(--text-muted)' }}>
      {failed
        ? 'Der Entwurf lässt sich nur im Portal öffnen. Die Anmeldung kam nicht an.'
        : 'Anmeldung wird übernommen …'}
    </div>
  )
}
